import React, { useState } from 'react';
import { X, CheckCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/Card';
import Button from '../../ui/Button';
import Input from '../../ui/Input';
import { WithdrawalRequest } from '../../../types';
import { useCompleteGameWithdrawalRecovery } from '../../../hooks/useApi';
import { getWithdrawalListLabel } from '../../../utils/withdrawalRequester';
import { formatWithdrawalDisplayAmount } from '../../../utils/portalUsdWithdrawalDisplay';
import { LoadingSpinner } from '../../ui/Loading';

interface CompleteRecoveryModalProps {
  withdrawal: WithdrawalRequest | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const parseErrorMessage = (error: any): string => {
  if (error?.response?.data?.message) {
    return error.response.data.message;
  }
  if (error?.message) {
    return error.message;
  }
  return 'An error occurred while completing game withdrawal recovery';
};

const CompleteRecoveryModal: React.FC<CompleteRecoveryModalProps> = ({
  withdrawal,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [adminNotes, setAdminNotes] = useState('');
  const completeRecoveryMutation = useCompleteGameWithdrawalRecovery();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!withdrawal) return;

    try {
      await completeRecoveryMutation.mutateAsync({
        transactionId: withdrawal.transactionId,
        data: {
          adminNotes: adminNotes.trim() || undefined,
        },
      });
      toast.success('Game withdrawal recovery completed');
      onSuccess?.();
      onClose();
      setAdminNotes('');
    } catch (error: any) {
      const errorMessage = parseErrorMessage(error);
      toast.error(errorMessage);
    }
  };

  const handleClose = () => {
    setAdminNotes('');
    onClose();
  };

  if (!isOpen || !withdrawal) return null;

  const phase3ErrorMessage =
    withdrawal.phase3Error ||
    (typeof withdrawal.syncMetadata?.phase3Error === 'string'
      ? withdrawal.syncMetadata.phase3Error
      : undefined) ||
    'Local settlement failed after automatic retries.';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 !mt-0">
      <Card className="w-full max-w-md">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle className="text-lg font-semibold">Complete Game Withdrawal Recovery</CardTitle>
          <Button variant="ghost" size="sm" onClick={handleClose}>
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                Provider already debited this amount. This action credits the player master wallet and finalizes local ledger entries. It does not call the game provider again.
              </p>
              <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg text-sm text-amber-900">
                {phase3ErrorMessage}
              </div>
              <div className="bg-gray-50 p-3 rounded-lg">
                <div className="text-sm">
                  <div className="font-medium">{withdrawal.transactionId}</div>
                  <div className="text-muted-foreground">
                    {getWithdrawalListLabel(withdrawal)}
                  </div>
                  <div className="font-medium text-red-600">
                    {formatWithdrawalDisplayAmount(withdrawal, { signed: true })}
                  </div>
                </div>
              </div>
            </div>

            <div>
              <label htmlFor="recoveryAdminNotes" className="block text-sm font-medium mb-2">
                Recovery Notes (Optional)
              </label>
              <Input
                id="recoveryAdminNotes"
                type="text"
                placeholder="Enter recovery notes..."
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                maxLength={1000}
              />
            </div>

            <div className="flex justify-end space-x-2 pt-4">
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={completeRecoveryMutation.isLoading}
                className="bg-amber-600 hover:bg-amber-700"
              >
                {completeRecoveryMutation.isLoading ? (
                  <div className="flex items-center">
                    <LoadingSpinner />
                    <span className="ml-2">Recovering...</span>
                  </div>
                ) : (
                  <div className="flex items-center">
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Complete Recovery
                  </div>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default CompleteRecoveryModal;
